import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, FormGroup } from 'reactstrap';
import { reduxForm, change, Field, formValueSelector } from 'redux-form';

import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_FORMAT } from '../../../../config/locale';
import Loader from '../../../common/Loader';
import SelectionableTable from '../../../common/forms/table/SelectionableTable';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from '../../../../config/constants';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import { ITINERARIES_FOR_COUNTER_COLUMNS } from '../../../../config/columns';
import {
  clearItineraries,
  searchItineraryBetweenDates,
} from '../../../../actions';
import ItinerarySearchForm from '../itinerary/ItinerarySearchForm';
import FormItem from '../../../common/forms/FormItem';
import { isRequired } from '../../../../utils/validators';
import TextInput from '../../../common/forms/input/TextInput';
import FormFooter from '../../../common/forms/FormFooter';
import CircuitSelect from '../../../common/forms/select/CircuitSelect';
import Select from '../../../common/forms/select/Select';
import ServiceTypeSelect from '../../../common/forms/select/ServiceTypeSelect';

const FORM_NAME = 'ItineraryChangeRouteForm';

const ItineraryChangeRouteForm = ({
  handleSubmit,
  onSubmit,
  dispatchClearItineraries,
  dispatchSearchItineraryBetweenDates,
  dispatchChange,
  itineraryList,
  loading,
  loadingActivity,
  newCircuitId,
}) => {
  const today = tzNormalizeDate();

  const searchItinerary = (searchFormValuesx) => {
    dispatchClearItineraries();
    dispatchSearchItineraryBetweenDates(searchFormValuesx);
  };

  const onSubmitToSearch = (formValues) => {
    const values = {
      sourceLocationId: formValues.sourceLocationId
        ? formValues.sourceLocationId.value
        : null,
      destinationLocationId: formValues.destinationLocationId
        ? formValues.destinationLocationId.value
        : null,
      fromDateString: tzNormalizeDate({
        date: formValues.fromDateString,
        format: INT_DATE_FORMAT,
      }),
      toDateString: tzNormalizeDate({
        date: formValues.toDateString,
        format: INT_DATE_FORMAT,
      }),
      size: MAX_PAGE_SIZE,
      directionType: formValues.directionType
        ? formValues.directionType.value
        : null,
      showNorteChico: formValues.showNorteChico || false,
      driverId: formValues.driver ? formValues.driver.value : null,
      cabinCrewId: formValues.cabinCrew ? formValues.cabinCrew.value : null,
      activeOnly: formValues.activeOnly !== false,
    };

    if (formValues.serviceType) {
      values.serviceTypeId = formValues.serviceType.value;
    }

    searchItinerary(values);
  };

  const routeOptions =
    newCircuitId && newCircuitId.routes ? newCircuitId.routes : [];

  return (
    <>
      <h3>Búsqueda de Itinerarios</h3>

      <ItinerarySearchForm
        showActiveOnlyField
        onSubmit={onSubmitToSearch}
        initialValues={{
          fromDateString: today,
          toDateString: today,
        }}
        sourceLocationIsRequired={false}
        destinationLocationIsRequired={false}
        searchByDateRange
        filterServiceType
        isClearable
      />

      <hr />

      <Form className="mt-3" onSubmit={handleSubmit(onSubmit)}>
        {loading && <Loader />}

        <SelectionableTable
          manual={false}
          columns={ITINERARIES_FOR_COUNTER_COLUMNS}
          data={itineraryList}
          loading={loading}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          filterable
          defaultFilterMethod={filterMethodCaseInsensitive}
          returnOnlySelectedItems
          keyField="id"
          form={FORM_NAME}
          name="itineraryIds"
          activeField={null}
        />

        <hr />

        <FormGroup row>
          <FormItem label="Nuevo Circuito" required>
            <Field
              name="newCircuitId"
              component={CircuitSelect}
              validate={[isRequired]}
              onChange={() => {
                dispatchChange(FORM_NAME, 'newRouteId', null);
              }}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Nueva Ruta" required>
            <Field
              name="newRouteId"
              component={Select}
              validate={[isRequired]}
              options={routeOptions}
              isDisabled={!newCircuitId}
              placeholder={
                newCircuitId
                  ? 'Seleccione una ruta'
                  : 'Seleccione primero un circuito'
              }
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Tipo de Servicio">
            <Field
              name="serviceTypeId"
              component={ServiceTypeSelect}
              isClearable
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Comentario" required>
            <Field
              name="comment"
              component={TextInput}
              type="textarea"
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>

        <FormFooter
          saveButtonDisabled={loadingActivity}
          saveButtonIcon={
            loadingActivity ? 'fa fa-spinner fa-spin' : 'fa fa-save'
          }
        />
      </Form>
    </>
  );
};

ItineraryChangeRouteForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dispatchClearItineraries: PropTypes.func.isRequired,
  dispatchSearchItineraryBetweenDates: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingActivity: PropTypes.bool,
  itineraryList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  ).isRequired,
  initialSearchValues: PropTypes.shape({}),
  newCircuitId: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    routes: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string,
      }),
    ),
  }),
};

ItineraryChangeRouteForm.defaultProps = {
  loading: false,
  loadingActivity: false,
  initialSearchValues: null,
  newCircuitId: null,
};

const selector = formValueSelector(FORM_NAME);

const mapDispatchToProps = {
  dispatchClearItineraries: clearItineraries,
  dispatchSearchItineraryBetweenDates: searchItineraryBetweenDates,
  dispatchChange: change,
};

const mapStateToProps = (state) => ({
  loading: state.ItineraryUnit.Itinerary.getIn(['all', 'loading']),
  loadingActivity: !state.ItineraryUnit.ItineraryChangeRoute.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  itineraryList: state.ItineraryUnit.Itinerary.getIn(['all', 'content']).toJS()
    .content,
  newCircuitId: selector(state, 'newCircuitId'),
});

const formComponent = reduxForm({
  form: FORM_NAME,
})(ItineraryChangeRouteForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
